import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import gemImg from '../assets/gem.webp';
import voucherImg from '../assets/art_voucher.webp';
import storybg from '../assets/storybg.webp';
import { GoldButton, fitScene } from './Shared';
import { CloseIcon } from './Icons';

/* ------------------------------------------------------------------
   Opening a Gem (prototype `gbx`): the stage covers the rewards page,
   the Gem lands and the user taps it three times — each tap draws a set
   of cracks, wobbles the Gem and throws sparks. The third tap charges it
   and fires onOpen(gemId) (the API); once that resolves the Gem shatters
   (shards, flash, shockwave, rays, confetti) and the reward rises in its
   place. "Open next Gem" resets the stage in place for the next one,
   "Collect" / "Done" / × close it. A failed open reports through onError
   and closes, leaving the Gem ready to try again.
   `others` is how many other Gems are still unopened and `nextId` the
   first of them. Laid out on the 375px stage inside the rewards page.
   ------------------------------------------------------------------ */

const TAPS = 3;
/* the Gem (180×218 at 97.5,300 → centre 187.5,409), title above and tap pill
   below live in a 468px scene (top:132 on the 812px design) that fits above
   the bottom-anchored CTA + Done (44 + 46 + a 26px gap) — see fitScene() */
const SCENE = { designTop: 132, height: 468, minTop: 60, bottom: 116 };
const CX = 187.5;
const CY = 409 - SCENE.designTop; // the Gem's centre in scene px
/* the crack paths drawn on tap 1, 2 and 3 (over the 160×194 Gem artwork) */
const CRACKS = [
  ['M82 64 L73 90 L86 106 L77 130', 'M73 90 L55 97'],
  ['M86 106 L106 115 L113 141', 'M77 130 L62 151', 'M82 64 L98 52'],
  ['M106 115 L128 104', 'M62 151 L47 162', 'M98 52 L108 34', 'M55 97 L38 91', 'M113 141 L120 166'],
];
const CONFETTI = ['#8A4BE0', '#B88AF5', '#5B2DA3', '#F4C04A', '#FFE08A', '#E9A93A'];
const TITLES = ['Tap the Gem to break it', 'Again!', 'One more!', 'Here it goes…'];
const TIPS = ['Tap to crack', 'Keep tapping', 'One more tap'];
const CLOSE_MS = 280;

const vib = (p) => { if (navigator.vibrate) { try { navigator.vibrate(p); } catch (e) { /* unsupported */ } } };
const cancelApi = (el) => {
  if (!el) return;
  el.getAnimations({ subtree: true }).forEach((a) => { if (!(window.CSSAnimation && a instanceof window.CSSAnimation)) a.cancel(); });
};
const GLOW = 'drop-shadow(0 0 22px rgba(233,166,255,.85)) drop-shadow(0 18px 24px rgba(0,0,0,.4))';

/* three gold bars for a Gold reward (the prototype's GOLDBARS) */
function GoldBars() {
  return (
    <svg viewBox="0 0 72 50" fill="none">
      <defs>
        <linearGradient id="gq-gb-bar" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0" stopColor="#FFE39A" /><stop offset=".5" stopColor="#F4B942" /><stop offset="1" stopColor="#C77A0C" />
        </linearGradient>
      </defs>
      <path d="M20 2h32l6 18H14l6-18Z" fill="url(#gq-gb-bar)" stroke="#8A5410" strokeWidth="1.2" />
      <path d="M6 28h32l6 18H0l6-18Z" fill="url(#gq-gb-bar)" stroke="#8A5410" strokeWidth="1.2" />
      <path d="M34 28h32l6 18H28l6-18Z" fill="url(#gq-gb-bar)" stroke="#8A5410" strokeWidth="1.2" />
    </svg>
  );
}

/* the reward's artwork by API `kind` */
function RewardArt({ kind }) {
  switch (kind) {
    case 'GOLD': return <div className="gq-ra gq-bars"><GoldBars /></div>;
    case 'SATS': return <div className="gq-ra gq-coin"><span>₿</span></div>;
    case 'CASHBACK': return <div className="gq-ra gq-coin"><span>₹</span></div>;
    case 'VOUCHER': return <img className="gq-ra gq-img" src={voucherImg} alt="" />;
    case 'BOOST': return <div className="gq-ra gq-boost">2×</div>;
    default: return <div className="gq-ra gq-coin"><span>★</span></div>;
  }
}

function TapIcon() {
  return (
    <svg className="gq-gb-tapic" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
      <path d="M9.5 12.5V4.75a1.75 1.75 0 0 1 3.5 0V11" />
      <path d="M13 10.25a1.75 1.75 0 0 1 3.5 0V12" />
      <path d="M16.5 11.25a1.75 1.75 0 0 1 3.5 0v3.25A6.5 6.5 0 0 1 13.5 21h-1.4a5.5 5.5 0 0 1-4.4-2.2l-3.1-4.1a1.6 1.6 0 0 1 2.4-2.1L9.5 15" />
    </svg>
  );
}

export default function GemBreak({ gemId, others = 0, nextId = null, onOpen, onError, onNext, onClose, stageHeight = 812 }) {
  const [taps, setTaps] = useState(0);
  const [stage, setStage] = useState('tap'); // tap | busy | won | closing
  const [won, setWon] = useState(null);      // { reward, at } once the Gem has opened
  const [tip, setTip] = useState(null);      // the tap pill's label, null while hidden

  const rootRef = useRef(null);
  const sceneRef = useRef(null);
  const gemRef = useRef(null);
  const auraRef = useRef(null);
  const imgRef = useRef(null);
  const crackRef = useRef(null);
  const shineRef = useRef(null);
  const shardsRef = useRef(null);
  const fxRef = useRef(null);
  const raysRef = useRef(null);
  const rwRef = useRef(null);
  const titleRef = useRef(null);
  const tok = useRef(0);        // bumps on every reset / close so stale sequences stop
  const tapsRef = useRef(0);
  const stageRef = useRef('tap');
  const timers = useRef([]);
  const tipTimer = useRef(null);
  const propsRef = useRef({});
  propsRef.current = { onOpen, onError, onNext, onClose, nextId, gemId };

  const later = useCallback((fn, ms) => { timers.current.push(setTimeout(fn, ms)); }, []);
  const setStageBoth = useCallback((s) => { stageRef.current = s; setStage(s); }, []);
  const fit = useMemo(() => fitScene(stageHeight, SCENE), [stageHeight]);
  const cy = fit.top + CY * fit.scale; // the Gem's centre on the stage, for the backdrop layers

  /* a stage point in scene px, or the Gem's centre */
  const local = (e) => {
    const r = sceneRef.current.getBoundingClientRect();
    const k = r.width / 375;
    return e && e.clientX != null ? { x: (e.clientX - r.left) / k, y: (e.clientY - r.top) / k } : { x: CX, y: CY };
  };

  const sparks = useCallback((x, y, n, r0 = 30, r1 = 120) => {
    const fx = fxRef.current;
    if (!fx) return;
    for (let i = 0; i < n; i += 1) {
      const s = document.createElement('i');
      s.className = 'gq-gb-sp';
      const sz = 3 + Math.random() * 5;
      s.style.cssText = `left:${x}px;top:${y}px;width:${sz}px;height:${sz}px`;
      fx.appendChild(s);
      const a = Math.random() * Math.PI * 2;
      const r = r0 + Math.random() * (r1 - r0);
      s.animate([
        { transform: 'translate(0,0)', opacity: 1 },
        { transform: `translate(${(Math.cos(a) * r).toFixed(0)}px,${(Math.sin(a) * r - 14).toFixed(0)}px)`, opacity: 0 },
      ], { duration: 600 + Math.random() * 400, easing: 'cubic-bezier(.1,.6,.3,1)', fill: 'forwards' }).onfinish = () => s.remove();
    }
  }, []);

  const showTip = useCallback((delay) => {
    clearTimeout(tipTimer.current);
    const t = tok.current;
    tipTimer.current = setTimeout(() => {
      if (t === tok.current && stageRef.current === 'tap' && tapsRef.current < TAPS) setTip(TIPS[tapsRef.current]);
    }, delay);
  }, []);

  /* fades the stage out and hands back to the rewards page */
  const close = useCallback(() => {
    if (stageRef.current === 'closing') return;
    tok.current += 1;
    clearTimeout(tipTimer.current);
    setTip(null);
    setStageBoth('closing');
    const root = rootRef.current;
    const fade = root.animate([{ opacity: 1 }, { opacity: 0 }], { duration: CLOSE_MS, fill: 'forwards' });
    fade.onfinish = () => { if (propsRef.current.onClose) propsRef.current.onClose(); };
  }, [setStageBoth]);

  /* the Gem bursts: shards fan out, a flash and shockwave, rays, sparks and confetti */
  const shatter = useCallback(() => {
    const gem = gemRef.current;
    const sh = shardsRef.current;
    const fx = fxRef.current;
    gem.style.visibility = 'hidden';
    const N = 11; const cx = 50; const cyy = 52; const pts = [];
    for (let i = 0; i < N; i += 1) {
      const a = -Math.PI / 2 + (i / N) * Math.PI * 2 + (i % 2 ? 0.12 : -0.08);
      pts.push([Math.max(0, Math.min(100, cx + Math.cos(a) * 62)), Math.max(0, Math.min(100, cyy + Math.sin(a) * 62))]);
    }
    for (let i = 0; i < N; i += 1) {
      const p1 = pts[i]; const p2 = pts[(i + 1) % N];
      const mx = (cx + p1[0] + p2[0]) / 3; const my = (cyy + p1[1] + p2[1]) / 3;
      const ang = Math.atan2(my - cyy, mx - cx);
      const d = 150 + Math.random() * 120;
      const rot = (Math.random() * 2 - 1) * 260;
      const im = document.createElement('img');
      im.src = gemImg;
      im.alt = '';
      im.style.clipPath = `polygon(${cx}% ${cyy}%, ${p1[0].toFixed(1)}% ${p1[1].toFixed(1)}%, ${p2[0].toFixed(1)}% ${p2[1].toFixed(1)}%)`;
      sh.appendChild(im);
      im.animate([
        { transform: 'translate(0,0) rotate(0) scale(1)', opacity: 1, filter: 'drop-shadow(0 0 10px rgba(233,166,255,.9)) brightness(2)' },
        { transform: `translate(${(Math.cos(ang) * d * 0.55).toFixed(0)}px,${(Math.sin(ang) * d * 0.55 - 30).toFixed(0)}px) rotate(${(rot * 0.5).toFixed(0)}deg) scale(1)`, opacity: 1, offset: 0.45, filter: 'drop-shadow(0 0 10px rgba(233,166,255,.9)) brightness(1.2)' },
        { transform: `translate(${(Math.cos(ang) * d).toFixed(0)}px,${(Math.sin(ang) * d + 120).toFixed(0)}px) rotate(${rot.toFixed(0)}deg) scale(.7)`, opacity: 0 },
      ], { duration: 1100 + Math.random() * 300, easing: 'cubic-bezier(.2,.7,.4,1)', fill: 'forwards' });
    }
    const fl = document.createElement('div');
    fl.className = 'gq-gb-fl';
    fx.appendChild(fl);
    fl.animate([{ transform: 'scale(.2)', opacity: 0 }, { transform: 'scale(1.3)', opacity: 1, offset: 0.3 }, { transform: 'scale(2)', opacity: 0 }], { duration: 700, easing: 'cubic-bezier(.2,.8,.3,1)', fill: 'forwards' });
    const sw = document.createElement('div');
    sw.className = 'gq-gb-sw';
    fx.appendChild(sw);
    sw.animate([
      { transform: 'scale(.4)', opacity: 0, borderWidth: '12px' },
      { transform: 'scale(.6)', opacity: 1, offset: 0.08 },
      { transform: 'scale(4.2)', opacity: 0, borderWidth: '1px' },
    ], { duration: 900, easing: 'cubic-bezier(.1,.6,.3,1)', fill: 'forwards' });
    raysRef.current.animate([
      { opacity: 0, transform: 'rotate(-10deg) scale(.5)' },
      { opacity: 1, transform: 'rotate(6deg) scale(1)', offset: 0.5 },
      { opacity: 0.6, transform: 'rotate(14deg) scale(1.04)' },
    ], { duration: 1400, fill: 'forwards', easing: 'cubic-bezier(.2,.7,.2,1)' });
    sparks(CX, CY, 26, 40, 170);
    for (let i = 0; i < 44; i += 1) {
      const e = document.createElement('i');
      e.className = 'gq-gb-cf';
      e.style.cssText = `left:${CX - 3}px;top:${CY - 10}px;background:${CONFETTI[i % CONFETTI.length]}`;
      fx.appendChild(e);
      const vx = (Math.random() * 2 - 1) * 280; const vy = -(360 + Math.random() * 340); const g = 900;
      const T = 1.9 + Math.random() * 0.6; const rz = (Math.random() * 2 - 1) * 720; const rx = 360 + Math.random() * 720;
      const frames = [];
      for (let q = 0; q <= 16; q += 1) {
        const t = (q / 16) * T; const drag = 1 - Math.min(0.55, t * 0.28);
        frames.push({
          offset: q / 16,
          transform: `translate(${(vx * t * drag + Math.sin(t * 9 + i) * 10).toFixed(1)}px,${(vy * t + 0.5 * g * t * t).toFixed(1)}px) rotate(${((rz * q) / 16).toFixed(0)}deg) rotateX(${((rx * q) / 16).toFixed(0)}deg)`,
          opacity: q === 0 ? 0 : (q > 13 ? (16 - q) / 3 : 1),
        });
      }
      e.animate(frames, { duration: T * 1000, delay: Math.random() * 120, easing: 'linear', fill: 'forwards' });
    }
    vib([0, 80, 30, 40]);
  }, [sparks]);

  /* the third tap: the Gem charges while the open request runs, then shatters
     on the answer (trembling until it lands if the network is slow) */
  const breakGem = useCallback(() => {
    const t = tok.current;
    const gem = gemRef.current;
    const req = Promise.resolve().then(() => propsRef.current.onOpen(propsRef.current.gemId));
    let settled = false;
    req.then(() => { settled = true; }, () => { settled = true; });
    const frames = [];
    for (let i = 0; i <= 14; i += 1) {
      const a = i / 14;
      frames.push({ transform: `translate(${(Math.sin(i * 2.3) * 7 * a).toFixed(1)}px,${(Math.cos(i * 1.7) * 5 * a).toFixed(1)}px) scale(${(1 + a * 0.14).toFixed(3)}) rotate(${(Math.sin(i * 1.9) * 6 * a).toFixed(1)}deg)` });
    }
    gem.animate(frames, { duration: 640, easing: 'linear', fill: 'forwards' });
    shineRef.current.animate([{ opacity: 0.66 }, { opacity: 1 }], { duration: 640, fill: 'forwards' });
    imgRef.current.animate([
      { filter: 'drop-shadow(0 0 34px rgba(245,215,255,1)) brightness(1.4)' },
      { filter: 'drop-shadow(0 0 60px rgba(255,240,255,1)) brightness(2.6)' },
    ], { duration: 640, fill: 'forwards' });
    vib([20, 40, 24, 36, 30, 30, 70]);
    later(async () => {
      if (t !== tok.current) return;
      let tremble = null;
      if (!settled) {
        tremble = gem.animate([
          { transform: 'translate(0,0) scale(1.14) rotate(0)' },
          { transform: 'translate(3px,-2px) scale(1.15) rotate(1.5deg)' },
          { transform: 'translate(-3px,2px) scale(1.14) rotate(-1.5deg)' },
          { transform: 'translate(2px,2px) scale(1.15) rotate(1deg)' },
          { transform: 'translate(0,0) scale(1.14) rotate(0)' },
        ], { duration: 260, iterations: Infinity, easing: 'linear' });
      }
      try {
        const result = await req;
        if (t !== tok.current) return;
        if (tremble) tremble.cancel();
        shatter();
        later(() => {
          if (t !== tok.current) return;
          setWon({ reward: result.reward, at: result.at });
          setStageBoth('won');
        }, 360);
      } catch (err) {
        if (t !== tok.current) return;
        if (tremble) tremble.cancel();
        if (propsRef.current.onError) propsRef.current.onError(err);
        close();
      }
    }, 640);
  }, [later, shatter, close, setStageBoth]);

  const tap = (e) => {
    if (stageRef.current !== 'tap' || tapsRef.current >= TAPS) return;
    const k = tapsRef.current + 1;
    tapsRef.current = k;
    setTaps(k);
    const pt = local(e);
    clearTimeout(tipTimer.current);
    setTip(null);
    if (k < TAPS) showTip(2600);
    vib(16 + k * 10);
    const gem = gemRef.current;
    crackRef.current.querySelectorAll(`.gq-c${k} path`).forEach((p, i) => {
      p.animate([{ strokeDashoffset: 1 }, { strokeDashoffset: 0 }], { duration: 260, delay: i * 45, fill: 'forwards', easing: 'cubic-bezier(.2,.8,.2,1)' });
    });
    const dir = k % 2 ? -1 : 1;
    gem.animate([
      { transform: 'scale(1) rotate(0)' },
      { transform: `scale(.87) rotate(${dir * 7}deg)`, offset: 0.28 },
      { transform: `scale(1.07) rotate(${-dir * 3}deg)`, offset: 0.62 },
      { transform: 'scale(1) rotate(0)' },
    ], { duration: 380, easing: 'cubic-bezier(.2,.8,.2,1)' });
    shineRef.current.animate([{ opacity: 0.18 * (k - 1) }, { opacity: 0.95 }, { opacity: 0.22 * k }], { duration: 420, fill: 'forwards' });
    imgRef.current.animate([
      { filter: `${GLOW} brightness(${1 + (k - 1) * 0.12})` },
      { filter: `drop-shadow(0 0 34px rgba(245,215,255,1)) drop-shadow(0 18px 24px rgba(0,0,0,.4)) brightness(${1.1 + k * 0.12})` },
    ], { duration: 380, fill: 'forwards' });
    sparks(pt.x, pt.y, 8 + k * 5);
    if (k === TAPS) {
      setStageBoth('busy');
      later(breakGem, 200);
    }
  };

  /* a fresh Gem (first open or "Open next Gem"): clear the last one's effects
     and play the entrance, in place so the page behind never shows through */
  useLayoutEffect(() => {
    tok.current += 1;
    const t = tok.current;
    timers.current.forEach(clearTimeout);
    timers.current = [];
    clearTimeout(tipTimer.current);
    const root = rootRef.current;
    cancelApi(root);
    shardsRef.current.innerHTML = '';
    fxRef.current.innerHTML = '';
    gemRef.current.style.visibility = '';
    tapsRef.current = 0;
    setTaps(0);
    setWon(null);
    setTip(null);
    setStageBoth('tap');
    root.animate([{ opacity: 0 }, { opacity: 1 }], { duration: 260 });
    gemRef.current.animate([
      { transform: 'scale(.3) rotate(-20deg)', opacity: 0 },
      { transform: 'scale(1.08) rotate(4deg)', opacity: 1, offset: 0.65 },
      { transform: 'scale(1) rotate(0)', opacity: 1 },
    ], { duration: 650, delay: 120, easing: 'cubic-bezier(.2,1.2,.3,1)', fill: 'backwards' });
    auraRef.current.animate([
      { opacity: 0.6, transform: 'scale(.95)' },
      { opacity: 1, transform: 'scale(1.06)' },
      { opacity: 0.6, transform: 'scale(.95)' },
    ], { duration: 2200, iterations: Infinity, easing: 'ease-in-out' });
    vib(10);
    showTip(900);
    return () => { if (t === tok.current) tok.current += 1; };
  }, [gemId, setStageBoth, showTip]);

  /* the reward rises into the Gem's place */
  useEffect(() => {
    if (!won) return;
    const rw = rwRef.current;
    const art = rw.querySelector('.gq-gb-art');
    rw.animate([{ opacity: 0 }, { opacity: 1 }], { duration: 200, fill: 'forwards' });
    art.animate([
      { transform: 'scale(.25) rotate(-25deg)', opacity: 0 },
      { transform: 'scale(1.12) rotate(6deg)', opacity: 1, offset: 0.6 },
      { transform: 'scale(.97) rotate(-2deg)', offset: 0.82 },
      { transform: 'scale(1) rotate(0)', opacity: 1 },
    ], { duration: 850, easing: 'cubic-bezier(.2,1,.3,1)', fill: 'both' });
    art.animate([{ translate: '0 0' }, { translate: '0 -6px' }, { translate: '0 0' }], { duration: 3200, delay: 900, iterations: Infinity, easing: 'ease-in-out' });
    [['.gq-gb-eye', 380], ['.gq-gb-n', 480], ['.gq-gb-s', 600]].forEach(([sel, d]) => {
      rw.querySelector(sel).animate([{ transform: 'translateY(12px)', opacity: 0 }, { transform: 'none', opacity: 1 }], { duration: 480, delay: d, fill: 'both', easing: 'cubic-bezier(.2,.9,.3,1)' });
    });
    titleRef.current.animate([{ opacity: 0 }, { opacity: 1 }], { duration: 300 });
  }, [won]);

  useEffect(() => () => {
    tok.current += 1;
    timers.current.forEach(clearTimeout);
    clearTimeout(tipTimer.current);
  }, []);

  const left = others + 1; // Gems to open, counting this one
  const eye = won ? (others ? `${others} MORE TO OPEN` : '') : (left > 1 ? `${left} GEMS TO OPEN` : 'YOUR GEM');
  const cta = others ? `Open next Gem · ${others} left` : 'Collect';
  const onCta = () => { if (nextId) onNext(nextId); else close(); };
  const cls = ['gq-gbx', won ? 'gq-won' : '', won && others ? 'gq-more' : ''].filter(Boolean).join(' ');

  return (
    <div className={cls} ref={rootRef} role="dialog" aria-modal="true" aria-label="Open your Gem">
      <div className="gq-gb-bg" aria-hidden="true">
        <div className="gq-gb-rot" style={{ top: cy }}><img src={storybg} alt="" /></div>
        <div className="gq-gb-rays" ref={raysRef} style={{ top: cy - 450 }} />
        <div className="gq-gb-glow" style={{ top: cy - 180 }} />
        <div className="gq-gb-vig" />
      </div>
      <button type="button" className="gq-gb-x" onClick={close} aria-label="Close"><CloseIcon /></button>

      <div className="gq-scene" ref={sceneRef} style={fit.style}>
        <div className="gq-gb-top">
          <div className="gq-gb-eye">{eye}</div>
          <div className="gq-gb-t" ref={titleRef} aria-live="polite">{won ? 'Gem opened!' : TITLES[taps]}</div>
          <div className="gq-gb-dots" aria-hidden="true">
            {[0, 1, 2].map((i) => <i key={i} className={i < taps ? 'gq-on' : ''} />)}
          </div>
        </div>

        <button type="button" className="gq-gb-gem" ref={gemRef} onClick={tap} aria-label={`Tap to crack the Gem, ${Math.max(0, TAPS - taps)} taps to go`} disabled={stage !== 'tap'}>
          <div className="gq-gb-gin">
            <div className="gq-gb-aura" ref={auraRef} />
            <img className="gq-gb-img" src={gemImg} alt="" ref={imgRef} />
            <svg className="gq-gb-crk" viewBox="0 0 160 194" preserveAspectRatio="none" ref={crackRef} aria-hidden="true">
              {CRACKS.map((group, k) => (
                <g className={`gq-c${k + 1}`} key={k}>
                  {group.map((d) => <path pathLength="1" d={d} key={d} />)}
                </g>
              ))}
            </svg>
            <div className="gq-gb-shine" ref={shineRef} />
          </div>
        </button>
        <div className="gq-gb-shards" ref={shardsRef} aria-hidden="true" />
        <div className="gq-gb-fx" ref={fxRef} aria-hidden="true" />

        <div className="gq-gb-rw" ref={rwRef} aria-live="polite">
          <div className="gq-gb-halo" />
          <div className="gq-gb-art">{won && <RewardArt kind={won.reward.kind} />}</div>
          <div className="gq-gb-eye">YOU WON</div>
          <div className="gq-gb-n">{won ? won.reward.name : ''}</div>
          <div className="gq-gb-s">{won ? won.reward.detail : ''}</div>
        </div>

        <div className={`gq-gb-tip${tip ? ' gq-show' : ''}`} aria-hidden="true">
          <div className="gq-ring" /><div className="gq-ring gq-r2" />
          <div className="gq-gb-pill"><TapIcon /><span>{tip || TIPS[0]}</span></div>
        </div>
      </div>

      <GoldButton className="gq-gb-cta" onClick={onCta} tabIndex={won ? 0 : -1}>{cta}</GoldButton>
      <button type="button" className="gq-gb-done" onClick={close} tabIndex={won && others ? 0 : -1}>Done</button>
    </div>
  );
}
